// Production-grade client-side monitoring and error tracking
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

interface MonitoringConfig {
  enabled: boolean;
  environment: string;
  sampleRate: number;
  debug: boolean;
}

const config: MonitoringConfig = {
  enabled: import.meta.env.PROD,
  environment: import.meta.env.MODE,
  sampleRate: 0.1,
  debug: import.meta.env.DEV,
};

let isInitialized = false;

// Initialize Sentry error tracking
export function initSentry() {
  if (isInitialized) return;
  
  try {
    Sentry.init({
      dsn: 'https://placeholder@sentry.io/placeholder', // Replace with actual DSN
      environment: config.environment,
      enabled: config.enabled,
      tracesSampleRate: config.sampleRate,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: false,
        }),
      ],
      beforeSend(event, hint) {
        // Filter sensitive data
        if (event.request) {
          delete event.request.cookies;
          if (event.request.headers) {
            delete event.request.headers['Authorization'];
          }
        }
        
        // Log to console in dev
        if (config.debug) {
          console.log('[Sentry] Event:', event);
        }
        
        return event;
      },
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error exception captured',
        'Network request failed',
        'Load failed',
      ],
    });
    
    isInitialized = true;
    console.log('[Monitoring] Sentry initialized');
  } catch (error) {
    console.error('[Monitoring] Failed to initialize Sentry:', error);
  }
}

// Track performance metrics
export function trackPerformance(
  metricName: string, 
  value: number, 
  metadata?: Record<string, unknown>
) {
  if (config.debug) {
    console.log('[Performance]', metricName, value, metadata);
  }

  // Add to Sentry performance monitoring
  Sentry.setMeasurement(metricName, value, 'millisecond');
  
  // Log to database for analytics
  if (config.enabled) {
    logMetricToDatabase(metricName, value, metadata);
  }
}

async function logMetricToDatabase(
  metricName: string, 
  value: number, 
  metadata?: Record<string, unknown>
) {
  try {
    await supabase.from('performance_metrics').insert([{
      metric_name: metricName,
      metric_type: 'client',
      metric_value: value,
      page_url: window.location.pathname,
      metadata: metadata ? JSON.parse(JSON.stringify(metadata)) : null,
    }]);
  } catch {
    // Silently fail - don't block user experience
  }
}

// Track user actions with business context
export function trackUserAction(
  action: string, 
  properties?: Record<string, unknown>
) {
  if (config.debug) {
    console.log('[User Action]', action, properties);
  }

  // Add breadcrumb for error context
  Sentry.addBreadcrumb({
    category: 'user-action',
    message: action,
    data: properties,
    level: 'info',
  });

  // Business metrics tracking
  const businessMetrics: Record<string, () => void> = {
    'checkout_started': () => trackConversionFunnel('checkout_start'),
    'checkout_completed': () => trackConversionFunnel('checkout_complete'),
    'add_to_cart': () => trackConversionFunnel('add_to_cart'),
    'product_viewed': () => trackConversionFunnel('product_view'),
    'subscription_started': () => trackConversionFunnel('subscription_start'),
  };

  businessMetrics[action]?.();
}

function trackConversionFunnel(step: string) {
  Sentry.setTag('funnel_step', step);
}

// API error tracking with detailed context
export function trackAPIError(
  endpoint: string,
  statusCode: number,
  errorMessage: string,
  requestDetails?: Record<string, unknown>
) {
  const errorData = {
    endpoint,
    statusCode,
    errorMessage,
    requestDetails,
    timestamp: new Date().toISOString(),
  };

  console.error('[API Error]', errorData);

  // Capture in Sentry with context
  Sentry.captureException(new Error(`API Error: ${endpoint} - ${statusCode}`), {
    tags: { 
      endpoint, 
      statusCode: statusCode.toString(),
      errorType: 'api_error',
    },
    extra: requestDetails,
    level: statusCode >= 500 ? 'error' : 'warning',
  });

  // Log critical errors to database
  if (statusCode >= 500) {
    logErrorToDatabase('api_error', errorMessage, errorData);
  }
}

async function logErrorToDatabase(
  errorType: string, 
  message: string, 
  metadata?: Record<string, unknown>
) {
  try {
    await supabase.from('error_logs').insert([{
      error_type: errorType,
      error_message: message.slice(0, 1000),
      severity: 'error',
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      metadata: metadata ? JSON.parse(JSON.stringify(metadata)) : null,
    }]);
  } catch {
    // Silently fail
  }
}

// Database query monitoring
export function trackDatabaseQuery(
  operation: string,
  table: string,
  duration: number,
  success: boolean
) {
  if (duration > 1000) {
    console.warn('[Slow Query]', { operation, table, duration: `${duration}ms` });
    
    Sentry.captureMessage(`Slow database query: ${table}`, {
      level: 'warning',
      tags: { operation, table },
      extra: { duration },
    });
  }

  trackPerformance(`db_query_${operation}`, duration, { table, success });
}

// Page view tracking
export function trackPageView(path: string, metadata?: Record<string, unknown>) {
  if (config.debug) {
    console.log('[Page View]', path, metadata);
  }

  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigate to ${path}`,
    level: 'info',
  });
}

// Set user context for error tracking
export function setUserContext(userId?: string, email?: string) {
  if (userId) {
    Sentry.setUser({ id: userId, email });
  } else {
    Sentry.setUser(null);
  }
}

// Health check with retry logic
export async function performHealthCheck(retries = 3): Promise<{
  healthy: boolean;
  latency: number;
  details?: Record<string, unknown>;
}> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const start = performance.now();
      const response = await fetch(
        'https://kllsxgvvwosxlcpxqwin.supabase.co/functions/v1/health-check',
        { 
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const latency = performance.now() - start;
      
      if (response.ok) {
        const data = await response.json();
        trackPerformance('health_check', latency, { status: 'healthy' });
        return { healthy: true, latency, details: data };
      }
      
      throw new Error(`Health check failed: ${response.status}`);
    } catch (error) {
      if (attempt === retries - 1) {
        console.error('[Health Check] Failed after retries:', error);
        return { healthy: false, latency: -1 };
      }
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
  
  return { healthy: false, latency: -1 };
}

// Web Vitals tracking
export function trackWebVitals() {
  if (typeof window === 'undefined') return;

  // Track Largest Contentful Paint
  const lcpObserver = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    const lastEntry = entries[entries.length - 1];
    trackPerformance('LCP', lastEntry.startTime);
  });
  lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

  // Track First Input Delay
  const fidObserver = new PerformanceObserver((entryList) => {
    const entries = entryList.getEntries();
    entries.forEach((entry) => {
      if ('processingStart' in entry) {
        const fid = (entry as PerformanceEventTiming).processingStart - entry.startTime;
        trackPerformance('FID', fid);
      }
    });
  });
  fidObserver.observe({ type: 'first-input', buffered: true });

  // Track Cumulative Layout Shift
  let clsValue = 0;
  const clsObserver = new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (!(entry as any).hadRecentInput) {
        clsValue += (entry as any).value;
      }
    }
    trackPerformance('CLS', clsValue * 1000); // Scale for visibility
  });
  clsObserver.observe({ type: 'layout-shift', buffered: true });
}

// Initialize all monitoring
export function initMonitoring() {
  initSentry();
  trackWebVitals();
  
  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      Sentry.addBreadcrumb({
        category: 'visibility',
        message: 'Page hidden',
        level: 'info',
      });
    }
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason, {
      tags: { type: 'unhandled_promise_rejection' },
    });
  });

  console.log('[Monitoring] All monitoring systems initialized');
}

export default {
  init: initMonitoring,
  initSentry,
  trackPerformance,
  trackUserAction,
  trackAPIError,
  trackDatabaseQuery,
  trackPageView,
  setUserContext,
  performHealthCheck,
  trackWebVitals,
};
